import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { api, ApiError } from "../lib/api"
import { useToast } from "../components/ui/Toast"
import type { AttemptInProgress, ContentMode, SimuladoMode } from "../types"

interface StartableProva {
  id: string
  active_attempt_id: string | null
}

interface StartOptions {
  mode?: SimuladoMode
  content_mode?: ContentMode
  /** Matérias do modo específico (o diagnóstico grátis é uma só). */
  subjects?: string[]
}

/**
 * Inicia (ou retoma) um simulado de uma prova específica.
 * `startingId` guarda o id da prova em processamento (pra spinner por box).
 */
export function useStartSimulado() {
  const [startingId, setStartingId] = useState<string | null>(null)
  const navigate = useNavigate()
  const { toast } = useToast()

  async function start(prova: StartableProva, opts: StartOptions = {}) {
    if (prova.active_attempt_id) {
      navigate(`/simulado/${prova.active_attempt_id}`)
      return
    }
    setStartingId(prova.id)
    try {
      const data = await api.post<{ attempt: AttemptInProgress }>("/api/attempts", {
        blueprint_id: prova.id,
        mode: opts.mode,
        content_mode: opts.content_mode,
        subjects: opts.subjects,
      })
      navigate(`/simulado/${data.attempt.id}`)
    } catch (e) {
      if (e instanceof ApiError && e.code === "subscription_required") {
        if (e.body.unconfirmed === true) {
          // Não confirmou o e-mail — o nivelamento está a um clique.
          // Dashboard tem o banner com reenvio.
          toast("Confirme seu e-mail pra liberar seu nivelamento", "info")
          navigate("/dashboard")
        } else {
          toast("Esse formato faz parte do plano de acesso", "info")
          navigate("/plano")
        }
      } else if (e instanceof ApiError && e.code === "attempt_in_progress" && typeof e.body.attempt_id === "string") {
        navigate(`/simulado/${e.body.attempt_id}`)
      } else if (e instanceof ApiError && e.code === "not_enough_questions") {
        toast("Banco de questões em atualização. Tente novamente em breve.", "error")
      } else {
        toast(e instanceof ApiError ? e.message : "Erro ao iniciar o simulado", "error")
      }
    } finally {
      setStartingId(null)
    }
  }

  return { start, startingId }
}
